import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { useAuthStore } from "../store/authStore";
import toast from "react-hot-toast";
import type { AxiosError } from "axios";

interface ApiErrorResponse {
  message?: string;
}

export default function EmailLogin() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"email" | "otp">("email");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const setToken = useAuthStore((state) => state.setToken);

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/request-otp", { email });
      toast.success("OTP sent to your email");
      setStep("otp");
       
    } catch (error) {
      const apiError = error as AxiosError<ApiErrorResponse>;
      const message = apiError.response?.data?.message || "Failed to request OTP";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(false);
    try {
      const res = await api.post("/auth/verify-otp", { email, otp });
      const { token } = res.data;

      const payload = JSON.parse(atob(token.split(".")[1]));
      const adminRoles = ["admin", "super_admin"];
      if (!adminRoles.includes(payload.role)) {
        toast.error("Access denied: Admins only");
        setLoading(false);
        return;
      }

      toast.success("Login successful");
      setToken(token, payload.role, "email", email);
      navigate("/");
       
    } catch (error) {
      const apiError = error as AxiosError<ApiErrorResponse>;
      const message = apiError.response?.data?.message || "Failed to verify OTP";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded shadow">
        <h2 className="text-center text-3xl font-extrabold text-gray-900">
          Admin Login (Development)
        </h2>
        <div className="text-sm text-gray-600 text-center bg-blue-50 p-3 rounded">
          💡 Dev mode: Use <code className="bg-gray-200 px-1 rounded">developer+admin@digitaldavid.io</code> with OTP <code className="bg-gray-200 px-1 rounded">123456</code>
        </div>
        {step === "email" ? (
          <form onSubmit={handleRequestOtp} className="mt-8 space-y-6">
            <input
              type="email"
              required
              className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed"
            >
              {loading ? "Sending..." : "Send OTP"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="mt-8 space-y-6">
            <input
              type="text"
              required
              className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              placeholder="OTP Code"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed"
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
